import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Button, Typography, makeStyles } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import { FormattedMessage, useIntl } from 'react-intl';
import { RateListTable } from '~/modules/rateCard/advancedRateCard/components/rateListTable';
import { CostOverrideRateEditor } from './cellEditors/CostOverrideRateEditor';

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: theme.spacing(2)
  },
  emptyText: {
    padding: theme.spacing(2, 1),
    fontStyle: 'italic',
    color: theme.palette.text.secondary
  },
  addFirstButton: {
    textTransform: 'none',
    padding: theme.spacing(0, 1),
    marginTop: theme.spacing(1)
  }
}));

const noop = () => {};
const EMPTY_ERRORS = {};
const COLUMN_RENDER_OVERRIDES = { amount: CostOverrideRateEditor };

const startIcon = <AddIcon />;

export const CostOverridesTable = ({
  selectedDimensions,
  projectRates,
  formOnChange,
  projectSlug,
  projectCurrency,
  addNewEntry,
  setAddNewEntry,
  errors = EMPTY_ERRORS,
  readOnly = false
}) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const firstDimensionLabel =
    selectedDimensions[0]?.displayText.toUpperCase() ?? '';
  const isEmpty = projectRates.length === 0;
  const columnPropOverrides = useMemo(
    () => ({
      amount: {
        value: formatMessage({
          id: 'projectCostOverrides.columnHeader.costOverride'
        }),
        align: 'left',
        onPercentChange: formOnChange.onPercentChange,
        onTypeChange: formOnChange.onTypeChange
      }
    }),
    [formatMessage, formOnChange.onPercentChange, formOnChange.onTypeChange]
  );

  return (
    <div className={classes.root}>
      <RateListTable
        projectRates={projectRates}
        projectDimensions={selectedDimensions}
        formOnChange={formOnChange}
        hasMore={false}
        loadingMore={false}
        loadMore={noop}
        errors={errors}
        openDeleteDialog={noop}
        projectSlug={projectSlug}
        addNewEntry={addNewEntry}
        setAddNewEntry={setAddNewEntry}
        projectCurrency={projectCurrency}
        readOnly={readOnly}
        firstRowDeletable
        columnRenderOverrides={COLUMN_RENDER_OVERRIDES}
        columnPropOverrides={columnPropOverrides}
      />
      {isEmpty ? (
        <Typography
          variant="body2"
          className={classes.emptyText}
          data-qe-id="CostOverridesTableInfoText"
        >
          <FormattedMessage id="projectCostOverrides.dialog.noCostOverridesSet" />
        </Typography>
      ) : null}
      {readOnly ? null : (
        <Button
          color="primary"
          size="small"
          startIcon={startIcon}
          className={classes.addFirstButton}
          onClick={formOnChange.onAddNewGroup}
          data-qe-id="CostOverridesTableAddButton"
        >
          {firstDimensionLabel}
        </Button>
      )}
    </div>
  );
};

CostOverridesTable.propTypes = {
  selectedDimensions: PropTypes.array.isRequired,
  projectRates: PropTypes.array.isRequired,
  formOnChange: PropTypes.object.isRequired,
  projectSlug: PropTypes.string,
  projectCurrency: PropTypes.object,
  addNewEntry: PropTypes.object,
  setAddNewEntry: PropTypes.func,
  errors: PropTypes.object,
  readOnly: PropTypes.bool
};

export default CostOverridesTable;
